import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider } from "@/context/AppContext";
import { StaffProvider } from "@/context/StaffContext";
import { Layout } from "@/components/Layout";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import Lookbook from "@/pages/Lookbook";
import StreetSheriff from "@/pages/StreetSheriff";
import Profile from "@/pages/Profile";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import { About, Contact, Shipping, Returns, Privacy, Terms, SizeGuide, CheckoutSuccess, CheckoutCancel, NotFound, Maintenance } from "@/pages/StaticPages";

import { StaffLogin, StaffRegister } from "@/pages/staff/StaffAuth";
import DashboardLayout from "@/pages/staff/DashboardLayout";
import Overview from "@/pages/staff/Overview";
import Inventory from "@/pages/staff/Inventory";
import AdminProducts from "@/pages/staff/AdminProducts";
import AdminOrders from "@/pages/staff/AdminOrders";
import { Members, Locations, StaffMgmt, AuditLog, StaffActivity, Waitlist, EmailLog } from "@/pages/staff/People";
import { Settings } from "@/pages/staff/Settings";

const Store = ({ children }) => <Layout>{children}</Layout>;

// Reversible storefront maintenance switch. Set to false to restore the public site.
const MAINTENANCE_MODE = false;
// Renders a public storefront page normally, or the Maintenance page when maintenance is on.
const Public = ({ children }) => (MAINTENANCE_MODE ? <Maintenance /> : <Store>{children}</Store>);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <StaffProvider>
          <AppProvider>
            <Toaster position="bottom-right" theme="dark" toastOptions={{ style: { background: "#11100f", border: "1px solid rgba(205,169,92,0.3)", color: "#f4f0e8" } }} />
            <Routes>
              {/* Storefront */}
              <Route path="/" element={<Public><Home /></Public>} />
              <Route path="/shop" element={<Public><Shop /></Public>} />
              <Route path="/product/:slug" element={<Public><Product /></Public>} />
              <Route path="/lookbook" element={<Public><Lookbook /></Public>} />
              <Route path="/street-sheriff" element={<Public><StreetSheriff /></Public>} />
              <Route path="/about" element={<Public><About /></Public>} />
              <Route path="/contact" element={<Public><Contact /></Public>} />
              <Route path="/profile" element={<Public><Profile /></Public>} />
              <Route path="/login" element={<Public><Login /></Public>} />
              <Route path="/register" element={<Public><Register /></Public>} />
              <Route path="/forgot-password" element={<Public><ForgotPassword /></Public>} />
              <Route path="/reset-password" element={<Public><ResetPassword /></Public>} />
              <Route path="/shipping" element={<Public><Shipping /></Public>} />
              <Route path="/returns" element={<Public><Returns /></Public>} />
              <Route path="/privacy" element={<Public><Privacy /></Public>} />
              <Route path="/terms" element={<Public><Terms /></Public>} />
              <Route path="/size-guide" element={<Public><SizeGuide /></Public>} />
              <Route path="/checkout/success" element={<Public><CheckoutSuccess /></Public>} />
              <Route path="/checkout/cancel" element={<Public><CheckoutCancel /></Public>} />
              <Route path="/maintenance" element={<Maintenance />} />

              {/* Staff / Operations */}
              <Route path="/staff/login" element={<StaffLogin />} />
              <Route path="/staff/register" element={<StaffRegister />} />
              <Route path="/staff/dashboard" element={<DashboardLayout />}>
                <Route index element={<Overview />} />
                <Route path="inventory" element={<Inventory />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="members" element={<Members />} />
                <Route path="waitlist" element={<Waitlist />} />
                <Route path="activity" element={<StaffActivity />} />
                <Route path="locations" element={<Locations />} />
                <Route path="staff" element={<StaffMgmt />} />
                <Route path="settings" element={<Settings />} />
                <Route path="email-log" element={<EmailLog />} />
                <Route path="audit" element={<AuditLog />} />
              </Route>

              <Route path="*" element={MAINTENANCE_MODE ? <Maintenance /> : <Store><NotFound /></Store>} />
            </Routes>
          </AppProvider>
        </StaffProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
